import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Clock, Mail, MapPin, Phone, RefreshCw } from 'lucide-react';
import { API } from '../../lib/constants';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

export const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language
      });
      toast.success(t.contact.success);
      setStatus({ type: 'success', message: t.contact.success });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t.contact.error);
      setStatus({ type: 'error', message: t.contact.error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <div className="energy-bar w-24 mb-6 mx-auto"></div>
          <p className="overline mb-4">{t.contact.label}</p>
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight uppercase">
            {t.contact.title}
          </h2>
          <p className="text-xl text-slate-600">
            {t.contact.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="minimal-card p-6 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">{t.contact.address}</h3>
                  <p className="text-slate-600">Padelarena Wädenswil</p>
                  <p className="text-slate-600">{t.contact.addressLine}</p>
                </div>
              </div>
            </div>

            <div className="minimal-card p-6 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">{t.contact.phone}</h3>
                  <a href="tel:+41438330834" className="text-slate-600 hover:text-slate-900 transition-colors inline-block py-1.5">
                    +41 43 833 0834
                  </a>
                </div>
              </div>
            </div>

            <div className="minimal-card p-6 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">{t.contact.email}</h3>
                  <a href="mailto:info@padelarena.ch" className="text-slate-600 hover:text-slate-900 transition-colors inline-block py-1.5">
                    info@padelarena.ch
                  </a>
                </div>
              </div>
            </div>

            <div className="minimal-card p-6 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-3">{t.contact.hours}</h3>
                  <p className="text-slate-600 text-sm mb-1">{t.contact.hoursWeekday}</p>
                  <p className="text-slate-600 text-sm">{t.contact.hoursWeekend}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 md:p-10 rounded-3xl border-2 border-blue-200 shadow-xl">
              <h3 className="text-2xl font-bold text-slate-900 mb-8">{t.contact.formTitle}</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    {t.contact.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 shadow-sm"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    {t.contact.emailLabel}
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 shadow-sm"
                    placeholder="ihre.email@beispiel.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                    {t.contact.phoneLabel}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 shadow-sm"
                    placeholder="+41 XX XXX XX XX"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                    {t.contact.message}
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 shadow-sm resize-none"
                    placeholder="Deine Nachricht hier..."
                    data-testid="contact-message-input"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="athletic-button athletic-button-primary w-full justify-center"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    t.contact.send
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Bot-protected WhatsApp Button
// - Layer 1 (invisible): real phone is base64-encoded; HTML source contains no wa.me URL
// - Layer 2 (1 click): tiny modal with honeypot + isTrusted + post-mount delay; humans pass instantly
